import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Compass, GraduationCap, ShieldCheck, Target } from "lucide-react";
import { Section, Eyebrow, Reveal } from "./shared";
import { VIDEO_JOBS } from "../../lib/site";

const icons = [Compass, GraduationCap, ShieldCheck, Target];
const flow = ["Attract", "Educate", "Build Trust", "Convert"];

export default function Ecosystem() {
  return (
    <Section id="system" bg="bg-brand-paleblue">
      <Reveal className="max-w-3xl">
        <Eyebrow color="#0B31EB">The System</Eyebrow>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.98] mt-5">
          What If Every Video<br />Had a Job?
        </h2>
        <p className="text-base sm:text-lg text-brand-ink/75 mt-5">
          Instead of publishing random content, build a connected content system where every video attracts, educates, builds trust, and moves the right viewer toward your business.
        </p>
      </Reveal>

      <div className="grid lg:grid-cols-2 gap-5 sm:gap-6 mt-12">
        {VIDEO_JOBS.map((v, i) => {
          const Icon = icons[i];
          return (
            <Reveal key={v.n} delay={(i % 2) * 0.08}>
              <div className="relative h-full rounded-[26px] border-2 border-brand-black bg-white p-6 sm:p-8 overflow-hidden transition-transform duration-300 hover:-translate-y-1.5"
                style={{ boxShadow: `8px 10px 0 ${v.color}` }}>
                <div className="flex items-start justify-between">
                  <span className="font-display text-2xl" style={{ color: v.color }}>{v.n}</span>
                  <span className="h-12 w-12 rounded-2xl flex items-center justify-center border-2 border-brand-black" style={{ background: v.color }}>
                    <Icon size={20} className={v.color === "#FFCB52" ? "text-brand-black" : "text-white"} />
                  </span>
                </div>
                <h3 className="font-display text-2xl sm:text-3xl mt-4">{v.t}</h3>
                <p className="text-sm sm:text-base text-brand-ink/75 mt-3 leading-relaxed">{v.d}</p>
                <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-brand-cream border-2 border-brand-black/10 px-4 py-2">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-brand-ink/50">Job</span>
                  <span className="text-sm font-semibold">{v.job}</span>
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>

      {/* Flow strip */}
      <Reveal delay={0.1}>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3 sm:gap-4">
          {flow.map((f, i) => (
            <React.Fragment key={f}>
              <span className="rounded-full bg-brand-black text-brand-cream px-5 py-2.5 font-bold text-sm sm:text-base">{f}</span>
              {i < flow.length - 1 && (
                <motion.span animate={{ x: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.6 }}>
                  <ArrowRight className="text-brand-blue" size={22} />
                </motion.span>
              )}
            </React.Fragment>
          ))}
        </div>
      </Reveal>

      <Reveal delay={0.15}>
        <p className="text-center font-serif-e text-xl sm:text-2xl md:text-3xl leading-snug mt-10 max-w-3xl mx-auto">
          When these videos work together, you no longer have random content. You have a complete{" "}
          <span className="font-bold text-brand-blue">YouTube Lead Generation System.</span>
        </p>
      </Reveal>
    </Section>
  );
}
